<script setup>
import { computed, nextTick, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';

import { menuSurfaceClass, menuItemClass } from 'shared/helpers/menuStyles';
import { onMenuKeydown } from './menuKeyboard';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import InboxChannelGlyph from 'dashboard/routes/dashboard/settings/inbox/components/InboxChannelGlyph.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  variant: {
    type: String,
    default: 'default',
    validator: value => ['default', 'compact', 'table-actions'].includes(value),
  },
  menuItems: {
    type: Array,
    default: () => [],
    validator: value => {
      return value.every(item => item.action && item.value && item.label);
    },
  },
  menuSections: {
    type: Array,
    default: () => [],
  },
  sectionsContainerClass: {
    type: [String, Array, Object],
    default: '',
  },
  sectionTitleClass: {
    type: [String, Array, Object],
    default: '',
  },
  showSectionSeparators: {
    type: Boolean,
    default: true,
  },
  thumbnailSize: {
    type: Number,
    default: 20,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  searchContainerClass: {
    type: [String, Array, Object],
    default: '',
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
  labelClass: {
    type: String,
    default: '',
  },
  disableLocalFiltering: {
    type: Boolean,
    default: false,
  },
  /** When `fixed`, pass pixel bounds via `floatingStyles` (e.g. teleported menus). */
  positionStrategy: {
    type: String,
    default: 'absolute',
    validator: v => ['absolute', 'fixed'].includes(v),
  },
  floatingStyles: {
    type: Object,
    default: () => ({}),
  },
  leadingVisualOnly: {
    type: Boolean,
    default: false,
  },
  iconClass: {
    type: String,
    default: '',
  },
  inboxGlyphSize: {
    type: String,
    default: 'xxs',
    validator: value => ['xxs', 'xs', 'sm', 'md', 'lg'].includes(value),
  },
  selectedIconClass: {
    type: String,
    default: '',
  },
  showItemIcons: {
    type: Boolean,
    default: true,
  },
  showEmptyState: {
    type: Boolean,
    default: true,
  },
  itemClass: {
    type: String,
    default: '',
  },
  selectedItemClass: {
    type: String,
    default: '',
  },
  itemLabelClass: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['action', 'search', 'itemHover']);

defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const attrs = useAttrs();

const searchQuery = ref('');
const menuElement = ref(null);
const previewRef = ref(null);
const previewContent = ref('');
const previewStyles = ref({});

const PREVIEW_WIDTH = 288;
const PREVIEW_GAP = 8;
const VIEWPORT_PADDING = 12;

const clamp = (value, minimum, maximum) =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

const getPreviewLeft = (targetRect, previewWidth = PREVIEW_WIDTH) => {
  const positionAfter = targetRect.right + PREVIEW_GAP;
  const positionBefore = targetRect.left - previewWidth - PREVIEW_GAP;
  const prefersBefore = document.documentElement.dir === 'rtl';
  let left = prefersBefore ? positionBefore : positionAfter;

  if (left + previewWidth > window.innerWidth - VIEWPORT_PADDING) {
    left = positionBefore;
  }

  if (left < VIEWPORT_PADDING) {
    left = positionAfter;
  }

  return clamp(
    left,
    VIEWPORT_PADDING,
    window.innerWidth - previewWidth - VIEWPORT_PADDING
  );
};

const showItemPreview = async (event, item) => {
  if (!item.preview) return;

  const targetRect = event.currentTarget.getBoundingClientRect();
  previewContent.value = item.preview;
  previewStyles.value = {
    left: `${getPreviewLeft(targetRect)}px`,
    top: `${Math.max(targetRect.top, VIEWPORT_PADDING)}px`,
  };

  await nextTick();

  if (!previewRef.value) return;

  const previewRect = previewRef.value.getBoundingClientRect();
  previewStyles.value = {
    left: `${getPreviewLeft(targetRect, previewRect.width)}px`,
    top: `${clamp(
      targetRect.top,
      VIEWPORT_PADDING,
      window.innerHeight - previewRect.height - VIEWPORT_PADDING
    )}px`,
  };
};

const hideItemPreview = () => {
  previewContent.value = '';
};

const handleItemHover = (event, item) => {
  emit('itemHover', item);
  showItemPreview(event, item);
};

const hasSections = computed(() => props.menuSections.length > 0);

const flattenedMenuItems = computed(() => {
  if (!hasSections.value) {
    return props.menuItems;
  }

  return props.menuSections.flatMap(section => section.items || []);
});

const filteredMenuItems = computed(() => {
  if (props.disableLocalFiltering) return props.menuItems;
  if (!searchQuery.value) return flattenedMenuItems.value;

  return flattenedMenuItems.value.filter(item =>
    item.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const filteredMenuSections = computed(() => {
  if (!hasSections.value) {
    return [];
  }

  if (props.disableLocalFiltering || !searchQuery.value) {
    return props.menuSections;
  }

  const query = searchQuery.value.toLowerCase();

  return props.menuSections
    .map(section => {
      const filteredItems = (section.items || []).filter(item =>
        item.label.toLowerCase().includes(query)
      );

      return {
        ...section,
        items: filteredItems,
      };
    })
    .filter(section => section.items.length > 0);
});

const handleSearchInput = event => {
  if (props.disableLocalFiltering) {
    emit('search', event.target.value);
  }
};

const handleAction = item => {
  const { action, value, ...rest } = item;
  emit('action', { action, value, ...rest });
};

const shouldShowEmptyState = computed(() => {
  if (hasSections.value) {
    return filteredMenuSections.value.length === 0;
  }

  return filteredMenuItems.value.length === 0;
});

const resolvedIconClass = computed(() => {
  if (props.iconClass) return props.iconClass;

  return props.variant === 'compact' ? 'size-5' : 'size-3.5';
});

const showLeadingIcons = computed(
  () => props.showItemIcons && props.variant !== 'table-actions'
);

defineExpose({ menuElement });
</script>

<template>
  <div
    ref="menuElement"
    v-bind="attrs"
    class="dropdown-menu z-50 flex flex-col min-w-[136px]"
    data-menu-list
    :class="[
      menuSurfaceClass,
      positionStrategy === 'fixed' ? 'fixed' : 'absolute',
      variant === 'table-actions'
        ? 'gap-0 px-0 py-2'
        : variant === 'compact'
          ? 'gap-2 px-1 pb-2'
          : 'gap-2 px-2 pb-2',
      {
        'pt-2': !showSearch && variant !== 'table-actions',
      },
    ]"
    :style="floatingStyles"
    @keydown="onMenuKeydown"
  >
    <slot name="header">
      <p
        v-if="title"
        class="mb-0 px-2 text-sm font-medium text-n-slate-11"
        :class="{ 'pt-2': showSearch }"
      >
        {{ title }}
      </p>
    </slot>
    <div
      v-if="showSearch"
      data-menu-search
      class="sticky top-0 z-20 bg-n-alpha-3 backdrop-blur-sm"
      :class="[{ 'pt-2': !title }, searchContainerClass]"
    >
      <Input
        v-model="searchQuery"
        type="search"
        :aria-label="searchPlaceholder || t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')"
        :placeholder="
          searchPlaceholder || t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')
        "
        autofocus
        @input="handleSearchInput"
      />
    </div>
    <slot name="content">
      <div
        v-if="hasSections"
        class="flex min-h-0 flex-1 flex-col overflow-y-auto"
        :class="sectionsContainerClass"
      >
        <template
          v-for="(section, sectionIndex) in filteredMenuSections"
          :key="section.title || sectionIndex"
        >
          <div
            v-if="section.title"
            class="sticky top-0 z-10 mb-0 bg-n-alpha-3 px-2 py-2 text-xs font-medium uppercase tracking-wide text-n-slate-11 backdrop-blur-sm"
            :class="sectionTitleClass"
          >
            {{ section.title }}
          </div>
          <div class="flex flex-col gap-1">
            <div
              v-if="section.isLoading"
              class="flex items-center justify-center py-2"
            >
              <Spinner :size="24" />
            </div>
            <div
              v-else-if="!section.items.length && section.emptyState"
              class="px-2 py-1.5 text-sm text-n-slate-11"
            >
              {{ section.emptyState }}
            </div>
            <button
              v-for="(item, itemIndex) in section.items"
              :key="item.value || itemIndex"
              type="button"
              class="group/menu-item z-60"
              :class="[
                menuItemClass,
                leadingVisualOnly || item.description
                  ? 'h-auto min-h-8 py-2'
                  : 'h-8',
                !showLeadingIcons && !item.thumbnail ? '!gap-0' : '',
                variant === 'table-actions'
                  ? '!h-auto !min-h-0 !rounded-none !px-4 !py-1 !text-[13px] !font-normal'
                  : '',
                itemClass,
                item.isSelected && selectedItemClass,
                {
                  'bg-n-alpha-1 dark:bg-n-solid-active': item.isSelected,
                  'text-n-ruby-11': item.action === 'delete',
                  'text-n-slate-12': item.action !== 'delete',
                },
              ]"
              :disabled="item.disabled"
              @mouseenter="handleItemHover($event, item)"
              @mouseleave="hideItemPreview"
              @focus="showItemPreview($event, item)"
              @blur="hideItemPreview"
              @click="handleAction(item)"
            >
              <slot name="thumbnail" :item="item">
                <Avatar
                  v-if="item.thumbnail"
                  :name="item.thumbnail.name"
                  :src="item.thumbnail.src"
                  :size="thumbnailSize"
                  :status="item.thumbnail.status"
                  :rounded-full="item.thumbnail.roundedFull !== false"
                />
              </slot>
              <template v-if="!leadingVisualOnly">
                <Icon
                  v-if="showLeadingIcons && item.icon"
                  :icon="item.icon"
                  class="flex-shrink-0"
                  :class="[
                    resolvedIconClass,
                    item.isSelected && selectedIconClass,
                  ]"
                />
                <InboxChannelGlyph
                  v-else-if="showLeadingIcons && item.inbox"
                  :inbox="item.inbox"
                  :size="inboxGlyphSize"
                  :class="item.isSelected && selectedIconClass"
                />
                <span
                  v-if="showLeadingIcons && item.emoji"
                  class="flex-shrink-0"
                  >{{ item.emoji }}</span
                >
                <span v-if="item.label" class="grid min-w-0 gap-0.5">
                  <span
                    class="min-w-0 truncate text-sm"
                    :class="[
                      labelClass,
                      itemLabelClass,
                      variant === 'table-actions' &&
                        '!text-[13px] !font-normal !leading-5',
                    ]"
                  >
                    {{ item.label }}
                  </span>
                  <span
                    v-if="item.description"
                    class="line-clamp-2 text-xs font-normal leading-4 text-n-slate-11"
                  >
                    {{ item.description }}
                  </span>
                </span>
                <Icon
                  v-if="item.trailingIcon && variant !== 'table-actions'"
                  :icon="item.trailingIcon"
                  class="ms-auto size-4 shrink-0 text-n-slate-10"
                />
              </template>
            </button>
          </div>
          <div
            v-if="
              showSectionSeparators &&
              sectionIndex < filteredMenuSections.length - 1
            "
            class="mx-2 my-1 h-px bg-n-weak"
          />
        </template>
      </div>
      <template v-else>
        <button
          v-for="(item, index) in filteredMenuItems"
          :key="index"
          type="button"
          class="group/menu-item z-60"
          :class="[
            menuItemClass,
            leadingVisualOnly || item.description
              ? 'h-auto min-h-8 py-2'
              : 'h-8',
            !showLeadingIcons && !item.thumbnail ? '!gap-0' : '',
            variant === 'table-actions'
              ? '!h-auto !min-h-0 !rounded-none !px-4 !py-1 !text-[13px] !font-normal'
              : '',
            itemClass,
            item.isSelected && selectedItemClass,
            {
              'bg-n-alpha-1 dark:bg-n-solid-active': item.isSelected,
              'text-n-ruby-11': item.action === 'delete',
              'text-n-slate-12': item.action !== 'delete',
            },
          ]"
          :disabled="item.disabled"
          @mouseenter="handleItemHover($event, item)"
          @mouseleave="hideItemPreview"
          @focus="showItemPreview($event, item)"
          @blur="hideItemPreview"
          @click="handleAction(item)"
        >
          <slot name="thumbnail" :item="item">
            <Avatar
              v-if="item.thumbnail"
              :name="item.thumbnail.name"
              :src="item.thumbnail.src"
              :size="thumbnailSize"
              :status="item.thumbnail.status"
              :rounded-full="item.thumbnail.roundedFull !== false"
            />
          </slot>
          <template v-if="!leadingVisualOnly">
            <Icon
              v-if="showLeadingIcons && item.icon"
              :icon="item.icon"
              class="flex-shrink-0"
              :class="[resolvedIconClass, item.isSelected && selectedIconClass]"
            />
            <InboxChannelGlyph
              v-else-if="showLeadingIcons && item.inbox"
              :inbox="item.inbox"
              :size="inboxGlyphSize"
              :class="item.isSelected && selectedIconClass"
            />
            <span v-if="showLeadingIcons && item.emoji" class="flex-shrink-0">{{
              item.emoji
            }}</span>
            <span v-if="item.label" class="grid min-w-0 gap-0.5">
              <span
                class="min-w-0 truncate text-sm"
                :class="[
                  labelClass,
                  itemLabelClass,
                  variant === 'table-actions' &&
                    '!text-[13px] !font-normal !leading-5',
                ]"
              >
                {{ item.label }}
              </span>
              <span
                v-if="item.description"
                class="line-clamp-2 text-xs font-normal leading-4 text-n-slate-11"
              >
                {{ item.description }}
              </span>
            </span>
            <Icon
              v-if="item.trailingIcon && variant !== 'table-actions'"
              :icon="item.trailingIcon"
              class="ms-auto size-4 shrink-0 text-n-slate-10"
            />
          </template>
        </button>
      </template>
    </slot>
    <div
      v-if="showEmptyState && shouldShowEmptyState"
      class="text-sm text-n-slate-11 px-2 py-1.5"
    >
      <slot name="empty-state">
        {{
          isSearching
            ? t('DROPDOWN_MENU.SEARCHING')
            : t('DROPDOWN_MENU.EMPTY_STATE')
        }}
      </slot>
    </div>
    <slot name="footer" />
  </div>

  <Teleport to="body">
    <div
      v-if="previewContent"
      ref="previewRef"
      role="tooltip"
      class="pointer-events-none fixed z-[9999] h-fit w-72 max-h-[calc(100vh-24px)] overflow-y-auto whitespace-pre-wrap rounded-lg bg-n-slate-12 px-3 py-2.5 text-start text-sm font-normal leading-5 text-n-slate-1 shadow-xl dark:bg-n-slate-1 dark:text-n-slate-12"
      :style="previewStyles"
    >
      {{ previewContent }}
    </div>
  </Teleport>
</template>
